<template>
  <div class="map-pane location-details-pane">
    <div
      class="map-pane-content unit-details-pane"
      :style="unitDetailsPaneStyle">
      <div class="unit-header">
        <div class="name-and-id">
          <span class="title">{{ activeEntity.name }}</span>
          <span class="id">
            <small>
              <entity-id-badge
                :id="`${activeEntity.externalID}`"
                class-badge="badge-white-map-panes"
                :description="$t('messages.clickToCopyTheXIDToTheClipboard', [entityBadgeTooltip])" />
            </small>
          </span>
        </div>
        <div
          v-if="activeEntities.length > 1"
          class="paging flex-v">
          <div class="flex-h">
            <button
              class="btn btn-xs btn-no-text btn-secondary-reverse-hollow mr-2"
              @click="$emit('goToPreviousCard')">
              <icon-previous-2 />
            </button>
            <button
              class="btn btn-xs btn-no-text btn-secondary-reverse-hollow"
              @click="$emit('goToNextCard')">
              <icon-next-2 />
            </button>
          </div>
          <span>
            {{ activeEntities.indexOf(activeEntity) + 1 }} {{ $t('messages.outOf') }} {{ activeEntities.length }}
          </span>
        </div>
      </div>

      <!-- SCREEN IMAGE -->
      <div
        v-if="clusteredType === MapInventoryType.SITES && siteImage != null"
        class="location-image">
        <div class="location-image-container">
          <img
            class="street-view-image"
            :src="siteImage"
            :alt="activeUnit.name" />
        </div>
      </div>
      <div
        v-else-if="
          clusteredType === MapInventoryType.UNITS && (activeUnit.imageUri || activeUnit.isStreetViewImageAvailable)
        "
        class="location-image">
        <div
          v-if="activeUnit.imageUri"
          class="location-image-container">
          <img
            class="street-view-image"
            :src="activeUnit.imageUri"
            :alt="activeUnit.name" />
        </div>
        <div
          v-else-if="activeUnit.isStreetViewImageAvailable"
          class="location-image-container">
          <img
            class="street-view-image"
            :src="activeUnit.streetViewImageUrl"
            :alt="activeUnit.name" />
          <div class="street-view-warning"><span>Google Street View imagery</span></div>
        </div>
      </div>

      <div
        v-if="clusteredType === MapInventoryType.UNITS && activeUnit"
        class="units-details">
        <div class="kpis">
          <kpi
            :label="$t('names.totalImpression', { n: 2 })"
            :value="formatNumber(totalImpressions, '0,0')"
            icon="icon-colored-impression"
            :is-money="true"
            :big-icon="true"
            :is-loading="isEstimateStatisticsLoading"
            data-entity-type="screen-impressions" />

          <kpi
            :label="$t('names.floorCPM', { n: 1 })"
            :value="activeUnit.floorCPMFormatted"
            icon="icon-colored-cpm"
            :is-money="true"
            :big-icon="true"
            data-entity-type="screen-floor-cpm" />
        </div>

        <!-- GENERAL INFO -->
        <div class="subsection">
          <div
            class="subsection-header"
            @click="isGeneralInfoExpanded = !isGeneralInfoExpanded">
            <div class="title">
              <icon-colored-summary />
              {{ capitalize($t('messages.generalInformation')) }}
            </div>
            <div class="collapse-container">
              <icon-section-expand v-if="!isGeneralInfoExpanded" />
              <icon-section-collapse v-if="isGeneralInfoExpanded" />
            </div>
          </div>
          <div
            v-if="isGeneralInfoExpanded"
            class="subsection-rows-container">
            <div
              v-if="(isDemandOrganization || isPublicPlan) && activeUnitMediaOwner"
              class="subsection-row logo">
              <label>{{ capitalize($t('names.mediaOwner', { n: 1 })) }}</label>
              <img
                v-if="activeUnitMediaOwner?.logoUrl != null"
                :src="activeUnitMediaOwner.logoUrl || null"
                :alt="activeUnitMediaOwner?.name || null" />
              <span v-else>
                {{ activeUnitMediaOwner?.name }}
              </span>
            </div>
            <div
              v-if="isSupplyOrganization && activeUnit.site != null"
              class="subsection-row">
              <label>{{ capitalize($t('names.site', { n: 1 })) }}</label>
              <span>{{ activeUnit.site.name }}</span>
            </div>
            <div
              v-if="activeUnit.mediaTypeID != null"
              class="subsection-row">
              <label>{{ capitalize($t('names.venueType', { n: 1 })) }}</label>
              <span>{{ venueTypeName }}</span>
            </div>
            <div
              v-if="activeUnitNetwork?.name !== activeUnitMediaOwner?.name"
              class="subsection-row">
              <label>{{ capitalize($t('names.network', { n: 1 })) }}</label>
              <span>{{ activeUnitNetwork.name }}</span>
            </div>
          </div>
        </div>

        <!-- IMPRESSIONS CAPACITY CHART -->
        <div class="subsection">
          <div
            class="subsection-header"
            @click="isScreensImpressionsCapacityExpanded = !isScreensImpressionsCapacityExpanded">
            <div class="title">
              <icon-colored-impression />
              {{ capitalize($t('messages.impressionCapacity')) }}
            </div>
            <div class="collapse-container">
              <icon-section-expand v-if="!isScreensImpressionsCapacityExpanded" />
              <icon-section-collapse v-if="isScreensImpressionsCapacityExpanded" />
            </div>
          </div>
          <div v-if="isScreensImpressionsCapacityExpanded">
            <impression-capacity-chart
              :flight-from="entity.startDate"
              :entity="activeUnit"
              :data="unitsHourlyImpressions" />
          </div>
        </div>

        <!-- SUPPORTED CONTENT -->
        <div class="subsection screen-dimensions-subsection">
          <div
            class="subsection-header"
            @click="isSupportedContentExpanded = !isSupportedContentExpanded">
            <div class="title">
              <icon-colored-missing-dimension />
              {{ capitalize($t('messages.supportedContent')) }}
            </div>
            <div class="collapse-container">
              <icon-section-expand v-if="!isSupportedContentExpanded" />
              <icon-section-collapse v-if="isSupportedContentExpanded" />
            </div>
          </div>
          <div
            v-if="isSupportedContentExpanded"
            class="screen-dimensions-container subsection-rows-container">
            <div class="screen-dimensions-frame">
              <div
                class="screen-dimensions"
                :style="activeUnitScreenDimensionStyle">
                <span>{{ formatNumber(activeUnit.screenWidth, '0,0') }}</span>
                <span>x</span>
                <span>{{ formatNumber(activeUnit.screenHeight, '0,0') }}</span>
              </div>
            </div>
            <div
              v-if="activeUnit.allowImage || activeUnit.allowVideo"
              class="supported-content">
              <div v-if="activeUnit.allowImage">
                <icon-colored-image />
                <span>{{ capitalize($t('names.image', { n: 2 })) }}</span>
              </div>
              <div v-if="activeUnit.allowVideo">
                <icon-colored-video />
                <span>{{ capitalize($t('messages.video', { n: 2 })) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- PLAYBACK -->
        <div class="subsection">
          <div
            class="subsection-header"
            @click="isPlaybackExpanded = !isPlaybackExpanded">
            <div class="title">
              <icon-colored-playback />
              {{ capitalize($t('names.playback', { n: 1 })) }}
            </div>
            <div class="collapse-container">
              <icon-section-expand v-if="!isPlaybackExpanded" />
              <icon-section-collapse v-if="isPlaybackExpanded" />
            </div>
          </div>
          <div
            v-if="isPlaybackExpanded"
            class="subsection-rows-container">
            <div class="subsection-row">
              <label>{{ capitalize($t('names.spotLength', { n: 1 })) }}</label>
              <span>{{ activeUnit.spotLengthFormatted }}</span>
            </div>
            <div
              v-if="isSupplyOrganization"
              class="subsection-row">
              <label>{{ capitalize($t('names.operatingHour', { n: 2 })) }}</label>
              <span v-sanitize-html="activeUnit.operatingHoursDescription" />
            </div>
            <div class="subsection-row">
              <label>{{ capitalize($t('names.timezone', { n: 1 })) }}</label>
              <span>{{ activeUnit.timezone }}</span>
            </div>
            <div
              v-if="languagesDescription"
              class="subsection-row">
              <label>{{ capitalize($t('names.language', { n: 2 })) }}</label>
              <span>{{ languagesDescription }}</span>
            </div>
            <div
              v-if="activeUnit.iabBlockCategories != null && activeUnit.iabBlockCategories.length > 0"
              class="subsection-row">
              <label>{{ capitalize($t('names.iabCategory', { n: 2 })) }}</label>
              <span>{{ activeUnit.iabBlockCategories.map((x) => x.description).join(', ') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="clusteredType === MapInventoryType.SITES && activeSite"
        class="sites-details">
        <div class="kpis">
          <kpi
            :label="$t('names.totalImpression', { n: 2 })"
            :value="formatNumber(totalImpressions, '0,0')"
            icon="icon-colored-impression"
            :is-money="true"
            :big-icon="true"
            :is-loading="isEstimateStatisticsLoading"
            data-entity-type="site-impressions" />

          <kpi
            :label="$t('names.network', { n: 2 })"
            :value="`${siteNetworksCount}`"
            icon="icon-colored-network"
            :is-money="false"
            :big-icon="true"
            data-entity-type="site-networks-count" />

          <kpi
            :label="$t('names.targetedScreen', { n: 2 })"
            :value="`${targetedScreens}`"
            icon="icon-colored-screen"
            :is-money="false"
            :big-icon="true"
            :is-loading="isEstimateStatisticsLoading"
            data-entity-type="site-targeted-screens" />

          <kpi
            :label="$t('names.floorCPM', { n: 1 })"
            :value="siteFloorCPMFormatted"
            icon="icon-colored-cpm"
            :is-money="true"
            :big-icon="true"
            data-entity-type="site-floor-cpm" />
        </div>

        <!-- NETWORKS -->
        <div
          v-if="siteNetworksCount !== 0"
          class="subsection">
          <span
            class="subsection-header network-title-chart"
            @click="isNetworksExpanded = !isNetworksExpanded">
            <div class="title">
              <icon-colored-network />
              {{ $t('names.network', { n: 2 }) }}
            </div>
            <div class="collapse-container">
              <icon-section-expand v-if="!isNetworksExpanded" />
              <icon-section-collapse v-if="isNetworksExpanded" />
            </div>
          </span>
          <div
            v-if="isNetworksExpanded"
            class="subsection-row chart-container networks-chart-container">
            <chart
              :mode="chartMode.NONE"
              :echarts-options="networkSplitChartOptions"
              :theme="chartTheme.TEN_TONES_SITES_NETWORK_PANE" />
          </div>
        </div>

        <!-- IMPRESSIONS CAPACITY CHART -->
        <div class="subsection">
          <div
            class="subsection-header"
            @click="isSitesImpressionsCapacityExpanded = !isSitesImpressionsCapacityExpanded">
            <div class="title">
              <icon-colored-impression />
              {{ capitalize($t('messages.impressionCapacity')) }}
            </div>
            <div class="collapse-container">
              <icon-section-expand v-if="!isSitesImpressionsCapacityExpanded" />
              <icon-section-collapse v-if="isSitesImpressionsCapacityExpanded" />
            </div>
          </div>
          <div v-if="isSitesImpressionsCapacityExpanded">
            <impression-capacity-chart
              :flight-from="entity.startDate"
              :entity="activeSite"
              :data="unitsHourlyImpressions" />
          </div>
        </div>

        <!-- VENUES -->
        <!-- CAM-2938: Stroer request. We keep it in case they want it back -->
        <!-- <div class="subsection" v-if="uniqueMediaTypeIDsFromSite?.length">
          <span class="subsection-header">{{ uppercase($t('names.venueType', { n: 2 })) }}</span>
          <div class="subsection-row">
            <ul class="subsection-content">
              <li v-for="i in uniqueMediaTypeIDsFromSite" :key="i">{{ venueTypeName(i) }}</li>
            </ul>
          </div>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
  import { capitalize } from 'vue';
  import { mapGetters } from 'vuex';

  import EntityIdBadge from '@/common/components/EntityIdBadge';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import ChartType from '@/common/enums/chart-type.enum';
  import { MapInventoryType } from '@/common/enums/map.enums';
  import {
    IconColoredImage,
    IconColoredImpression,
    IconColoredMissingDimension,
    IconColoredNetwork,
    IconColoredPlayback,
    IconColoredSummary,
    IconColoredVideo,
  } from '@/common/icons/colored';
  import { IconNext2, IconPrevious2, IconSectionCollapse, IconSectionExpand } from '@/common/icons/monochrome';
  import Filter from '@/common/models/query-params/Filter';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import { uppercase } from '@/globals';
  import Kpi from '@/org-app/components/controls/KPI';
  import ImpressionCapacityChart from '@/org-app/components/inventory/overallMap/ImpressionCapacityChart.vue';
  import { waitUntil } from '@/utils/DelayHelper';
  import MapDataProvider from '@/utils/MapDataProvider';
  import formatNumber from '@/utils/NumberFormatter';
  import i18n from '@/vueI18n';

  export default {
    components: {
      EntityIdBadge,
      IconColoredImage,
      IconColoredImpression,
      IconColoredMissingDimension,
      IconColoredNetwork,
      IconColoredPlayback,
      IconColoredSummary,
      IconColoredVideo,
      IconPrevious2,
      IconNext2,
      IconSectionCollapse,
      IconSectionExpand,
      ImpressionCapacityChart,
      Kpi,
    },
    props: {
      showNetworks: {
        type: Boolean,
      },
      networkCountByClusterPerSite: {
        type: Object,
        default: null,
      },
      activeUnits: {
        type: Array,
        default: () => [],
      },
      activeUnit: {
        type: Object,
        default: null,
      },
      mediaOwners: {
        type: Array,
        default: () => [],
      },
      networks: {
        type: Array,
        default: () => [],
      },
      mediaTypes: {
        type: Array,
        default: () => [],
      },
      unitDetailsPaneStyle: {
        type: String,
        default: '',
      },
      activeUnitScreenDimensionStyle: {
        type: String,
        default: '',
      },
      clusteredType: {
        type: String,
        default: '',
      },
      activeSites: {
        type: Array,
        default: () => [],
      },
      activeSite: {
        type: Object,
        default: null,
      },
      colorPerNetworkID: {
        type: Array,
        default: () => [],
      },
      siteImage: {
        type: String,
        default: null,
      },
      entity: {
        type: Object,
        default: null,
      },
    },
    emits: ['goToPreviousCard', 'goToNextCard'],
    data() {
      return {
        chartMode: ChartMode,
        chartTheme: ChartTheme,
        activeEntity: {},
        activeEntities: [],
        isPlaybackExpanded: false,
        isNetworksExpanded: true,
        isGeneralInfoExpanded: true,
        isSupportedContentExpanded: false,
        isSitesImpressionsCapacityExpanded: true,
        isScreensImpressionsCapacityExpanded: true,
        isEstimateStatisticsLoading: false,
        mapDataProviderInternal: null,
        MapInventoryType,
        networkSplitChartOptions: {},
        unitsHourlyImpressions: [],
        unitIDsFromSite: [],
        targetedScreens: 0,
        totalImpressions: 0,
      };
    },
    computed: {
      ...mapGetters('orgApp', ['isSupplyOrganization', 'isDemandOrganization']),
      isPublicPlan() {
        return this.mapDataProvider?.isPublicPlan || false;
      },
      uniqueMediaTypeIDsFromSite() {
        if (this.clusteredType === MapInventoryType.SITES) {
          return [
            ...new Set(
              this.activeUnits.filter((unit) => unit.siteID === this.activeSite.id).map((unit) => unit.mediaTypeID),
            ),
          ].filter((mediaType) => mediaType);
        }
        return [...new Set(this.activeSites.mediaTypeIDs)];
      },
      minFloorCPMFromSite() {
        if (this.clusteredType === MapInventoryType.SITES) {
          return Math.min(
            ...this.activeUnits.filter((unit) => unit.siteID === this.activeSite.id).map((unit) => unit.floorCPM),
          );
        }
        return Math.min(...this.activeUnits.map((unit) => unit.floorCPM));
      },
      maxFloorCPMFromSite() {
        if (this.clusteredType === MapInventoryType.SITES) {
          return Math.max(
            ...this.activeUnits.filter((unit) => unit.siteID === this.activeSite.id).map((unit) => unit.floorCPM),
          );
        }
        return Math.max(...this.activeUnits.map((unit) => unit.floorCPM));
      },
      networksByCount() {
        if (this.activeSites == null) {
          return [];
        }

        const count = {};

        if (this.activeSites) {
          if (this.showNetworks) {
            if (this.networkCountByClusterPerSite[this.activeSite.id] == null) return {};

            for (const [networkID, networkCount] of this.networkCountByClusterPerSite[this.activeSite.id]) {
              const networkFound = this.networks.find((network) => network.id === parseInt(networkID, 10));
              const name = networkFound.name.length > 20 ? networkFound.code : networkFound.name;
              if (networkFound) count[name] = { count: networkCount, id: networkID };
            }
          } else {
            this.activeSites.networkIDs.forEach((networkID) => {
              const networkFound = this.networks.find((x) => x.id === networkID);
              if (networkFound) count[networkFound.name] = { count: 1, id: networkID };
            });
          }
        }
        return count;
      },
      entityBadgeTooltip() {
        return this.clusteredType === MapInventoryType.SITES
          ? i18n.global.t('names.site', { n: 1 })
          : i18n.global.t('names.screen', { n: 1 });
      },
      siteFloorCPMFormatted() {
        const minFloorCPM = i18n.global.n(this.minFloorCPMFromSite, 'currency');
        const maxFloorCPM =
          this.minFloorCPMFromSite !== this.maxFloorCPMFromSite
            ? `- ${i18n.global.n(this.maxFloorCPMFromSite, 'currency')}`
            : '';
        return `${minFloorCPM} ${maxFloorCPM}`;
      },
      siteNetworksCount() {
        return Object.keys(this.networksByCount).length;
      },
      activeUnitMediaOwner() {
        return this.mediaOwners.find((x) => x.id === this.activeUnit.ownerID);
      },
      activeUnitNetwork() {
        return this.activeUnit?.network || this.networks.find((x) => x.id === this.activeUnit.networkID);
      },
      languagesDescription() {
        return this.activeUnit?.languagesDescription;
      },
      venueTypeName() {
        return (
          this.activeUnit.mediaType?.displayName ||
          this.mediaTypes
            .reduce((mem, x) => [...mem, ...x.children], [])
            .find((x) => x.id === this.activeUnit.mediaTypeID)?.name
        );
      },
    },
    watch: {
      activeUnit() {
        this.activeEntity = this.activeUnit;
        this.activeEntities = this.activeUnits;
        this.updateChart();
        this.updateImpressionCapacity();
      },
      activeSite() {
        this.activeEntity = this.activeSite;
        this.activeEntities = this.activeSites;
        this.updateChart();
        this.updateImpressionCapacity();
      },
    },
    async mounted() {
      await waitUntil(() => this.entity);
      this.mapDataProvider = new MapDataProvider(this.entity);

      if (this.clusteredType === MapInventoryType.SITES) {
        this.activeEntity = this.activeSite;
        this.activeEntities = this.activeSites;
      } else {
        this.activeEntity = this.activeUnit;
        this.activeEntities = this.activeUnits;
      }
      this.updateImpressionCapacity();
      this.initScreensBySitesChart();
    },
    methods: {
      capitalize,
      formatNumber,
      uppercase,
      initScreensBySitesChart() {
        const titleChart = this.showNetworks
          ? i18n.global.t('messages.networksBySite', { n: 1 })
          : i18n.global.t('messages.screensBySites');
        const seriesName = this.showNetworks ? capitalize(i18n.global.t('names.networkName')) : titleChart;

        const getImpressions = (id) => {
          const network = this.impressionsByNetwork.find((x) => x.network_id.toString() === id);
          return formatNumber(network.impressions, '0a').length === 2
            ? formatNumber(network.impressions, '0.0a')
            : formatNumber(network.impressions, '0a');
        };

        const tooltipFormatted = (params) => {
          const { name } = params;
          const screens = `${capitalize(i18n.global.t('names.screen', { n: 2 }))}: ${params.data.value}`;
          const impressions = `${capitalize(i18n.global.t('names.impression', { n: 2 }))}: ${getImpressions(params.data.value1)}`;
          return `${name}<br/>${screens}<br/>${impressions}`;
        };

        this.networkSplitChartOptions = {
          calculable: true,
          tooltip: {
            show: true,
            formatter: (params) => tooltipFormatted(params),
            confine: true,
            backgroundColor: 'rgba(50,50,50,0.7)',
          },
          series: [
            {
              name: seriesName,
              type: ChartType.PIE,
              radius: this.showNetworks ? ['40%', '65%'] : '65%',
              data: [],
              center: ['50%', '60%'],
              label: {
                formatter: '{b} ({c})',
              },
              height: 200,
              startAngle: 0,
            },
          ],
          legend: {
            show: true,
            bottom: 0,
          },
          animationType: 'scale',
          animationEasing: 'elasticOut',
        };

        this.updateChart();
      },
      updateChart() {
        this.networkSplitChartOptions.series[0].data = [];

        for (const [name, value] of Object.entries(this.networksByCount)) {
          this.networkSplitChartOptions.series[0].data.push({
            name,
            value: value.count,
            value1: value.id,
            itemStyle: { color: this.colorPerNetworkID.find((x) => x.id.toString() === value.id).color },
          });
        }
      },
      hourlyToDaily(hourlyValues) {
        const dailyValues = [];
        const hoursPerDay = 24;
        const days = hourlyValues.length / hoursPerDay;

        for (let i = 0; i < days; i += 1) {
          const startIndex = i * hoursPerDay;
          const endIndex = startIndex + hoursPerDay;
          const dailyValue = hourlyValues.slice(startIndex, endIndex).reduce((a, b) => a + b, 0);
          dailyValues.push(dailyValue);
        }
        return dailyValues;
      },
      async getEstimateStatistics() {
        this.isEstimateStatisticsLoading = true;

        const params = this.entity.targetParamsEstimatedDeliveryStatistics;
        if (this.clusteredType === MapInventoryType.UNITS) {
          params.unit_targeting.unit_ids = [this.activeEntity.id];
        } else {
          params.unit_targeting.unit_ids = this.unitIDsFromSite;
          params.with_impressions_by_network_id = true;
        }

        const result = await this.mapDataProvider.getEstimatedDeliveryStatistics({ params });
        this.targetedScreens = result?.unitCount;
        this.totalImpressions = result?.impressions;
        this.impressionsByNetwork = result?.impressionsByNetworkID;

        this.isEstimateStatisticsLoading = false;
      },
      async getUnitsImpressions() {
        const unitIDs = this.clusteredType === MapInventoryType.UNITS ? [this.activeUnit.id] : this.unitIDsFromSite;
        const params = {
          date_from: this.entity.startDate,
          date_to: this.entity.endDate,
          unit_ids: unitIDs,
          demographic_ids: this.entity.demographicIDs,
          ad_duration: this.entity.adDuration,
          screen_frequency_cap: this.entity.minSecondsBetweenPlays,
        };

        const result = await this.mapDataProvider.getUnitsHourlyImpressions(params);
        this.unitsHourlyImpressions = result && this.totalImpressions > 0 ? this.hourlyToDaily(result) : [];
      },
      async getUnitIDsFromSite() {
        this.unitIDsFromSite = [];
        const params = QueryParams.for({
          top: null,
          showLoader: false,
          filterCollection: new FilterCollection([
            new Filter('site_id', [this.activeEntity.id], FilterComparisonOperator.IN),
          ]),
        });

        const result = await this.mapDataProvider.getUnits(params);

        if (result) {
          this.unitIDsFromSite = result.items.map((x) => x.id);
        }
      },
      async updateImpressionCapacity() {
        await this.getUnitIDsFromSite();
        await this.getEstimateStatistics();
        await this.getUnitsImpressions();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  #app {
    .location-details-pane {
      height: 100%;
    }

    .map-pane {
      border-radius: 0.6rem !important;
      overflow: hidden;

      .map-pane-content {
        font-size: 0.9em;
        display: flex;
        flex-direction: column;
        -webkit-overflow-scrolling: touch;
        transform-origin: top;
        animation-name: scaleVertical;
        animation-duration: 0.5s;
        animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
        animation-fill-mode: backwards;
        overflow-y: auto;

        &.no-scroll {
          overflow: visible;
        }

        &.padded {
          padding: 0.6rem;
        }
      }

      .unit-header,
      .site-header {
        background-color: var(--secondaryColor);
        color: var(--secondaryColorReverse);

        padding: 1rem 2rem;
        font-weight: 700;
        font-size: 1.4rem;
        display: flex;
        flex-direction: row;
        flex: 1 0 auto;
        position: relative;
        overflow: visible;
        box-shadow: 0 1px 6px rgba(0, 0, 0, 0.25);
        z-index: 1;
        line-height: 1.8rem;

        .name-and-id {
          display: flex;
          flex-grow: 1;
          flex-direction: column;

          .id {
            margin-top: 0.5rem;
          }

          small {
            margin-top: 0.3rem;
            font-weight: 400;
          }
        }

        .paging {
          align-items: flex-end;
          font-size: 0.8em;

          .btn {
            margin-bottom: 0.2rem;
          }

          span {
            text-align: right;
          }
        }

        > svg {
          fill: var(--secondaryColorReverse);
          stroke: var(--secondaryColorReverse);

          min-width: 2.4rem;
          width: 2.4rem;
          height: 2.4rem;
          margin-right: 1.6rem;
        }

        .back-btn {
          background: var(--actionsColorReverse);
          color: var(--actionsColor);

          cursor: pointer;
          border-radius: 2rem;
          border-width: 0;
          display: flex;
          width: 2.8rem;
          height: 2.8rem;
          outline: none;
          box-shadow: $shadow;
          min-width: auto;
          margin-right: 0.8rem;
          margin-top: -0.4rem;
          position: relative;

          &:hover {
            background: var(--actionsColor);

            svg {
              fill: var(--actionsColorReverse);
              stroke: var(--actionsColorReverse);
            }
          }

          svg {
            fill: var(--actionsColor);
            stroke: var(--actionsColor);
            display: block;
            margin: auto;
            height: 1.6rem;
            width: 1.6rem;
            transform: rotate(90deg);
          }
        }

        .circle-btn {
          z-index: 1;
          width: 3.7rem;
          height: 3.7rem;
          margin-left: 22.8rem;
          animation-delay: 0.5s;
          position: absolute;
          right: -18px;
          margin-top: 0;
        }
      }

      .location-image {
        width: 100%;
        height: 25rem;
        margin-bottom: 1rem;

        .location-image-container {
          width: 100%;
          height: 100%;
          position: relative;

          .street-view-image {
            object-fit: contain;
            max-width: 100%;
            height: 100%;
            width: 100%;
            display: block;
          }

          .street-view-warning {
            color: rgba(0, 0, 0, 0.5);
            background-color: color.adjust(#fff, $alpha: 0.5) !important;
            position: absolute;
            bottom: 0.8rem;
            left: 50%;
            width: 18rem;
            padding: 0.1rem 0rem;
            border-radius: 1.6rem;
            margin: auto auto auto -9rem;
            text-align: center;
            font-size: 1.1rem;

            span {
              text-shadow: 0 0 0.5rem #fff;
            }
          }
        }
      }

      .sites-details,
      .units-details {
        margin: 0 1.6rem 1.6rem;
        .kpis {
          display: flex;
        }
        .kpi {
          overflow: hidden;
          .kpis {
            border-radius: 0.5rem;
          }
        }
      }

      .units-details .kpi {
        justify-content: center;
      }

      .sites-details .kpis {
        .kpi {
          flex-basis: 40%;
          padding-left: 2rem;
        }
      }

      .subsection {
        padding: 0;
        border: 0.1rem solid #eee;
        .subsection-header {
          background-color: var(--paneHeaderBackgroundColor);
          color: var(--textColorDark);
          margin: 0 0 1rem;
          padding: 1rem 1.6rem;
          position: relative;
          font-weight: 600;
          border: none;
          border-bottom: solid 0.1rem rgba(0, 0, 0, 0.02);
          box-shadow: none;
          top: 0;
          border-radius: 0;
          font-size: 1.2rem;
          display: flex;
          justify-content: space-between;

          svg {
            height: 2.2rem;
            position: relative;
            top: 0.5rem;
            left: 0.2rem;
            margin-right: 0.5rem;
          }

          &:has(.collapse-container) {
            margin: 0;
            cursor: pointer;
          }

          .collapse-container {
            svg {
              stroke: var(--textColor);
              width: 1.6rem;
              height: 1.6rem;
            }
          }
        }

        .networks-chart-container {
          padding-bottom: 2rem;
        }

        .subsection-rows-container {
          padding: 1.6rem 1.6rem;
          background: var(--panesBackgroundColor);
          font-size: 1.26rem;

          .subsection-row {
            &.logo {
              img {
                max-width: 50%;
                max-height: 2.5rem;
                margin: auto 0 auto auto;
              }
            }
          }
        }

        .supported-content {
          display: flex;
          justify-content: space-evenly;
          align-items: center;

          div {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-right: 2rem;

            svg {
              width: 3.2rem;
              height: 3.2rem;
            }

            span {
              font-size: 0.9em;
              font-weight: 600;
              margin-top: 0.3rem;
            }
          }
        }

        .screen-dimensions-container {
          display: flex;
          padding: 1.6rem 1.6rem 1.6rem;
          justify-content: space-evenly;
          margin: 0;

          .screen-dimensions-frame {
            border: solid 0.1rem rgba(0, 0, 0, 0.15);
            background: var(--secondaryColorReverseFaded50);
            padding: 0.8rem;
            box-shadow: $small-shadow;

            .screen-dimensions {
              border: solid 0.1rem var(--secondaryColorFaded50);
              background: var(--secondaryColorReverse);
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;

              span {
                color: var(--secondaryColor);
                font-size: 1.1rem;
                text-align: center;

                &:nth-of-type(2n + 1) {
                  font-size: 1.6rem;
                }

                &:nth-of-type(2n) {
                  margin: 0 1rem;
                }
              }
            }
          }
        }
      }

      :deep(.echarts) {
        margin: 0;
      }

      .networks-chart-container {
        :deep(.echarts) {
          margin-top: -2rem;
        }
      }

      :deep(.kpi) {
        .icon {
          margin-right: 1.2rem;
          svg {
            width: 3.5rem;
            height: 3.5rem;
          }
        }

        .right {
          .label {
            font-size: 1.2rem;
          }
          .value-container {
            font-size: 1.8rem;
          }
        }
      }
    }
  }
</style>
